import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import {
  ArrowForwardRounded, AllInclusiveRounded, VerifiedRounded, DevicesRounded, PaymentsRounded,
  TravelExploreRounded, DashboardCustomizeRounded, CheckRounded, WidgetsRounded,
} from '@mui/icons-material';
import { BRAND } from '../../theme';
import { TEMPLATES } from '../templates';
import TemplateGallery from '../TemplateGallery';
import TemplatePreview from '../TemplatePreview';
import { BUILDER_URL } from '../SiteLayout';

const sectionSx = { px: { xs: 2, md: 4 }, scrollMarginTop: 80 };
const inner = { maxWidth: 1240, mx: 'auto' };

// Counts up to `to` once the number scrolls into view.
const CountUp = ({ to, duration = 1400 }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        setValue(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(frame); };
  }, [to, duration]);

  return <span ref={ref}>{value}</span>;
};

// Laptop mockup whose screen cycles through a few templates.
const Laptop = () => {
  const showcase = TEMPLATES.filter(t => t.popular);
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(n => (n + 1) % showcase.length), 3500);
    return () => clearInterval(id);
  }, [showcase.length]);

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 880, mx: 'auto' }}>
      <Box sx={{
        bgcolor: '#1c1a2e', borderRadius: '18px 18px 6px 6px', p: { xs: 1, md: 1.5 },
        boxShadow: '0 40px 80px rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <Box sx={{ borderRadius: 1.5, overflow: 'hidden', position: 'relative' }}>
          {showcase.map((t, n) => (
            <Box key={t.slug} sx={{
              position: n === 0 ? 'relative' : 'absolute', inset: 0,
              opacity: n === i ? 1 : 0, transition: 'opacity .8s ease',
            }}>
              <TemplatePreview template={t} ratio={0.6} />
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{
        height: { xs: 10, md: 16 }, mx: '-6%', borderRadius: '0 0 18px 18px',
        background: 'linear-gradient(180deg, #d9dbe6 0%, #a9adbf 100%)',
      }} />
      <Box sx={{ height: 24, mx: '8%', background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.35), transparent 70%)' }} />
    </Box>
  );
};

const FEATURES = [
  { icon: AllInclusiveRounded, title: 'Lifetime free updates', text: 'New templates and features land in your account automatically.' },
  { icon: VerifiedRounded, title: 'One-time setup', text: 'Pick a template, add your products and go live the same day.' },
  { icon: DevicesRounded, title: 'Looks great everywhere', text: 'Every template is responsive on phones, tablets and desktops.' },
  { icon: PaymentsRounded, title: 'Payments built in', text: 'Accept UPI, cards and wallets with Razorpay checkout.' },
  { icon: TravelExploreRounded, title: 'SEO ready', text: 'Custom titles, descriptions and share images for every page.' },
  { icon: DashboardCustomizeRounded, title: 'One admin for everything', text: 'Products, orders, customers and FAQs in a single dashboard.' },
];

const PLAN_POINTS = ['All templates included', 'Unlimited products', 'Orders & payments dashboard', 'Free updates forever'];

const Home = () => (
  <>
    {/* Hero */}
    <Box sx={{
      ...sectionSx, position: 'relative', overflow: 'hidden', color: '#fff',
      background: BRAND.heroGradient, pt: { xs: 16, md: 20 }, pb: { xs: 8, md: 10 },
    }}>
      {/* Particle field */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, opacity: 0.5,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)',
        backgroundSize: '34px 34px',
        maskImage: 'radial-gradient(ellipse at 50% 30%, #000 0%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, #000 0%, transparent 70%)',
      }} />
      <Box aria-hidden sx={{
        position: 'absolute', width: 520, height: 520, borderRadius: '50%', top: -160, right: -120,
        background: 'radial-gradient(circle, rgba(123,63,242,0.55), transparent 70%)',
      }} />
      <Box sx={{ ...inner, position: 'relative', textAlign: 'center' }}>
        <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 44, md: 76 }, lineHeight: 1.05, letterSpacing: '-0.03em', mb: 2.5 }}>
          Make it{' '}
          <Box component="span" sx={{ background: 'linear-gradient(90deg,#b99bff,#7fb0ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            possible
          </Box>
        </Typography>
        <Typography sx={{ fontSize: { xs: 17, md: 20 }, color: 'rgba(255,255,255,0.72)', maxWidth: 620, mx: 'auto', mb: 4.5 }}>
          Enjoy the journey of building a new website. Pick a ready-made template, make it yours and start selling.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: { xs: 7, md: 9 } }}>
          <Button component={RouterLink} to="/templates" variant="contained" size="large" endIcon={<ArrowForwardRounded />} sx={{ px: 4, py: 1.5 }}>
            Explore templates
          </Button>
          <Button component={RouterLink} to="/login" size="large"
            sx={{ px: 4, py: 1.5, color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' } }}>
            Start building
          </Button>
        </Box>
        <Laptop />
      </Box>
    </Box>

    {/* Template count + gallery */}
    <Box id="templates" sx={{ ...sectionSx, py: { xs: 8, md: 12 } }}>
      <Box sx={inner}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 18 }}>with Anainfo</Typography>
          <Typography sx={{
            fontWeight: 800, fontSize: { xs: 72, md: 110 }, lineHeight: 1, letterSpacing: '-0.04em',
            background: BRAND.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            <CountUp to={TEMPLATES.length} />+
          </Typography>
          <Typography variant="h5" sx={{ mt: 1 }}>Complete website templates, right behind a click</Typography>
        </Box>
        <TemplateGallery limit={9} />
      </Box>
    </Box>

    {/* Features */}
    <Box id="features" sx={{ ...sectionSx, py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
      <Box sx={inner}>
        <Typography variant="h3" sx={{ textAlign: 'center', fontSize: { xs: 30, md: 44 }, mb: 1.5 }}>Everything you need to launch</Typography>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mb: 7, maxWidth: 560, mx: 'auto' }}>
          Every template comes with the same powerful store, admin and payments under the hood.
        </Typography>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <Box key={title} sx={{ p: 3.5, borderRadius: 4, border: `1px solid ${BRAND.line}`, transition: 'box-shadow .2s', '&:hover': { boxShadow: BRAND.softShadow } }}>
              <Box sx={{ width: 52, height: 52, borderRadius: 3, background: BRAND.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: BRAND.glow }}>
                <Icon sx={{ color: '#fff' }} />
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 15 }}>{text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* Pricing CTA */}
    <Box id="pricing" sx={{ ...sectionSx, py: { xs: 8, md: 12 } }}>
      <Box sx={{
        ...inner, borderRadius: 6, overflow: 'hidden', position: 'relative', color: '#fff',
        background: BRAND.heroGradient, p: { xs: 4, md: 8 },
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 5, alignItems: 'center',
      }}>
        <Box>
          <Typography variant="h3" sx={{ fontSize: { xs: 30, md: 44 }, mb: 2 }}>Start building your website now</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, fontSize: 17 }}>
            One simple plan with every template included. Building for many businesses? Contact us for bulk pricing.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/login" variant="contained" size="large" sx={{ px: 4, py: 1.5 }}>Get started</Button>
            <Button component={RouterLink} to="/templates" size="large"
              sx={{ px: 4, py: 1.5, color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)' }}>
              Browse templates
            </Button>
          </Box>
        </Box>
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, p: 4 }}>
          {PLAN_POINTS.map(p => (
            <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CheckRounded sx={{ color: '#9fb8ff' }} />
              <Typography sx={{ fontSize: 16 }}>{p}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* Builder promo */}
    <Box sx={{ ...sectionSx, pb: { xs: 8, md: 12 } }}>
      <Box sx={{ ...inner, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4, p: { xs: 4, md: 6 }, borderRadius: 6, bgcolor: '#fff', boxShadow: BRAND.softShadow }}>
        <Box sx={{ width: 84, height: 84, borderRadius: 4, background: BRAND.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: BRAND.glow }}>
          <WidgetsRounded sx={{ color: '#fff', fontSize: 44 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
          <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>Meet</Typography>
          <Typography variant="h4" sx={{ fontSize: { xs: 26, md: 34 } }}>Our AI Site Builder</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>Describe your business and get a ready-to-edit website in minutes.</Typography>
        </Box>
        <Button href={BUILDER_URL} variant="contained" size="large" endIcon={<ArrowForwardRounded />} sx={{ px: 4, py: 1.5 }}>
          Learn more
        </Button>
      </Box>
    </Box>
  </>
);

export default Home;
